<template>
  <canvas ref="canvasRef" :width="width" :height="height" class="sign-board"></canvas>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { getUrl, fileFolder } from '@/util/resource'
import { ElMessage } from 'element-plus'
import { fabric } from 'fabric'
import { newCall } from '@/util/http'
import { debounce, dataURLtoFile, isEmpty } from '@safety/shared-utils'
import { imageFitCanvas } from '@/util/fab'

defineExpose({ useDefault, reset, upload })

const props = defineProps({
  width: {
    type: Number,
    default: 600
  },
  height: {
    type: Number,
    default: 300
  },
  /**
   * 是否设置为默认签名,提交时使用
   */
  asDefault: {
    type: Boolean,
    default: false
  }
})

const canvasRef = ref(null)

const model = defineModel({
  /**
   * 签名文件，类型为base64编码的dataURL
   */
  type: String,
  default: null
})

/**
 * 默认签名
 */
const defaultValue = ref('')

/**
 * 默认签名是否已加载
 * 防止重复加载
 */
const defaultLoaded = ref(false)

/**
 * 是否已使用默认签名
 */
const defaultUsed = ref(false)

/**
 * fabric画布实例
 */
const fab = ref(null)

onMounted(() => {
  fab.value = initCanvas()
})

onUnmounted(() => {
  fab.value.dispose()
})

function initCanvas() {
  const canvas = new fabric.Canvas(canvasRef.value, {
    isDrawingMode: true,
    enableRetinaScaling: true,
    imageSmoothingEnabled: true
  })
  canvas.freeDrawingBrush = new fabric.PencilBrush(canvas)
  canvas.freeDrawingBrush.width = 2
  canvas.on('path:created', onPathCreated)
  return canvas
}

const onPathCreated = debounce(() => {
  model.value = getDataUrl()
}, 300)

async function useDefault() {
  if (defaultUsed.value) return

  if (!defaultLoaded.value) {
    const { code, data, message } = await newCall({ url: 'user/get/sign', loading: true })
    if (code !== 0) {
      ElMessage.error(message)
      return
    }
    defaultLoaded.value = true
    if (isEmpty(data)) {
      ElMessage.error('无默认签名')
    } else {
      defaultValue.value = data
    }
  }

  if (isEmpty(defaultValue.value)) return

  fabric.Image.fromURL(
    getUrl(fileFolder.SIGN_IMAGE, defaultValue.value),
    (img, isError) => {
      if (isError) {
        ElMessage.error('加载默认签名失败')
        return
      }
      imageFitCanvas(img, fab.value)
      fab.value.clear()
      fab.value.add(img)
      fab.value.renderAll()
      defaultUsed.value = true
      model.value = getDataUrl()
    },
    { crossOrigin: 'anonymous' }
  )
}

async function upload() {
  if (model.value === null) {
    return { ok: false, message: '请先签名' }
  }

  const setAsDefault = props.asDefault

  const formData = new FormData()
  formData.append('asDefault', setAsDefault)
  formData.append('file', dataURLtoFile(model.value, 'signature'))

  const { code, data, message } = await newCall({
    url: 'user/set/sign',
    method: 'POST',
    headers: { 'Content-Type': false },
    body: formData,
    loading: true
  })
  if (code === 0) {
    if (setAsDefault) defaultValue.value = data
    return {
      ok: true,
      filename: data,
      rollback: async () => await newCall({ url: 'user/delete/sign', method: 'POST', body: { filename: data } })
    }
  } else {
    return { ok: false, message }
  }
}

function getDataUrl() {
  if (fab.value.getObjects().length === 0) {
    return null
  }
  try {
    return fab.value.toDataURL()
  } catch (err) {
    console.error(err)
    ElMessage.error('导出签名失败')
    return null
  }
}

function reset() {
  fab.value.clear()
  defaultUsed.value = false
  model.value = null
}
</script>

<style scoped lang="scss">
.sign-board {
  border: var(--my-border);
  border-radius: 5px;
  box-sizing: border-box;
}
</style>
